import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./LessonProgressModal.module.scss";
import LessonModal from "./modal/LessonModal";
import { getMediaAbsolutePath } from "../../services/File";
import Spinner from "../spinner/Spinner";
import OverviewForm from "./overview-form/OverviewForm";
import {
  CourseContentProvider,
  useStandaloneContent,
} from "@hooks/useStandaloneContent";
import {
  FeedbackForm,
  LessonStatus,
  MyCourseEnum,
  SllLesson,
  SllModule,
} from "@src/types";
import Button from "../button/Button";
import LessonForm from "./lesson-form/LessonForm";
import LessonPreView from "./lesson-form/preview/LessonPreView";
import SelectedContent from "../lesson-details/selected-content/SelectedContent";
import {
  getContentIcon,
  sortByAlphabetic,
  sortByPosition,
} from "@utils/helpers";
import {
  IconLesson,
  IconRateLesson,
  auProgramGradeLevels,
  createPrettyURL,
  createStudentLink,
  createURL,
  standaloneLessonsSelCompetencies,
  lessonTypeLabels,
  countryOptions,
  SURVEY_TYPE,
  IconProjRequired,
} from "@src/constants";
import {
  resolveMixpanelProgramName,
  mixpanelMultiCollectionProps,
  resolveMixpanelStandaloneGrade,
} from "@src/utils/mixpanelProgramType";
import { mixpanelCompetencyProps } from "@src/utils/mixpanelCompetency";
import { mixpanelSkillProps } from "@src/utils/mixpanelSkill";
import { mixpanelMoodProps } from "@src/utils/mixpanelMood";
import { resolveStandaloneMixpanelLessonType } from "@src/utils/mixpanelLessonType";
import Card from "./card/Card";
import {
  useSllGetLastVisitedModuleLazyQuery,
  useSllGetLessonInfoLazyQuery,
  SllCourse,
  useSllGetPreProgramInfoLazyQuery,
  useSllPreProgramQuizLazyQuery,
  ContentType,
  useGenerateParentLinkForUserMutation,
  FeedbackFormContent,
  AbsentStudentLesson,
  useFeedbackFormLazyQuery,
  useCancelFeedbackFormMutation,
} from "@src/api/graphql-codegen";
import ResumeModal from "../unit-progress-modal/resume-modal/ResumeModal";
import { useHistory, useLocation } from "react-router-dom";
import FSBackIcon from "@src/assets/icons/arrow_back.svg?react";
import OutlineIcon from "src/assets/icons/outline.svg?react";
import TicketIcon from "src/assets/icons/ticket.svg?react";
import { Menu, Tooltip } from "antd";
import {
  useToast,
  useQueryDictionary,
  useQueryParams,
  useUser,
  useMarkLessonComplete,
  useAnalytics,
  Events,
  useExitTicketLocalStorage,
  exitDocumentFullscreen,
} from "@src/hooks";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import ChevronDown from "src/assets/icons/dropdown.svg?react";
import ShareIcon from "src/assets/icons/new-share.svg?react";
import GreenShareIcon from "src/assets/icons/share-green.svg?react";
import BurgerIcon from "src/assets/icons/burger.svg?react";
import NormalCheckIcon from "src/assets/icons/mark_check_normal.svg?react";
import { LoadingOutlined } from "@ant-design/icons";
import QuizPreview from "./lesson-form/preview/QuizPreview";
import { LordIcon } from "../lord-icon/lord-icon";
import { ClockCircleOutlined } from "@ant-design/icons";
import LinkShareIcon from "./cover-video/LinkShareIcon";
import { CopyToClipboard } from "@src/utils/componentCasts";
import { Widget } from "@typeform/embed-react";
import NormalModal from "../modal/normal-modal/NormalModal";
import GenerateExitTicketLinkModal from "../program-details/exitticket-modal/GenerateExitTicketLinkModal";
import ShareModal from "../modal/share-modal/ShareModal";
import SllMaterialPreview from "./material-form/MaterialPreview";
import RequiredLessonMaterials from "../lesson-details/lessonmaterials-modal/RequiredLessonMaterials";
import CheatSheetSection from "../lesson-details/lessonmaterials-modal/CheatSheetSection";
import { shouldShowLessonMaterialsCheatSheet } from "../lesson-details/lessonmaterials-modal/lessonMaterialsCheatSheet";
import GenerateStudentLinkModal from "../studentlink-modal/GenerateStudentLinkModal";
import { usePresentationContext } from "@src/hooks/usePresentationContext";
import { logger } from "../../utils/logger";

type Props = {
  open: boolean;
  lessonId: string;
  lesson?: SllLesson;
  lessonlist?: SllLesson[];
  onClose: () => void;
  courseMode?: string;
  course?: SllCourse;
  isParentApp?: boolean;
  addLessonToCollection?: () => void;
  coursesForSelectedLesson?: SllCourse[];
  gotoCollection?: (studyPlan: SllCourse) => void;
  isPlatformTower?: boolean;
  isAusPreProgramQuiz?: boolean;
  programId?: string;
  programName?: string;
  showBackButton?: boolean;
  /** Mixpanel open event. Defaults to standaloneLessonOpen. Pass false to skip. */
  fireEvent?: string | false;
  gradeName?: string;
  mcId?: string;
  mcName?: string;
  /** Grade carried on a shared link, naming the card the lesson was opened from. */
  launchedGrade?: string;
  collectionId?: string;
  collectionName?: string;
};

function LessonProgressComponent(props: Props) {
  const {
    open,
    lessonId,
    lessonlist,
    onClose,
    course,
    isParentApp,
    courseMode,
    addLessonToCollection,
    coursesForSelectedLesson,
    gotoCollection,
    isPlatformTower = false,
    isAusPreProgramQuiz = false,
    programName,
    showBackButton = true,
    fireEvent,
    gradeName,
    programId,
    mcId,
    mcName,
    launchedGrade,
    collectionId,
    collectionName,
  } = props;

  // Default open tracking for all lesson opens; false / special modes opt out
  const openTrackEvent =
    fireEvent === false || isPlatformTower || isAusPreProgramQuiz
      ? undefined
      : (fireEvent ?? Events.actions.standaloneLessonOpen);

  const history = useHistory();
  const { pathname } = useLocation();
  const params = useQueryParams();
  const isMobile = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const regexs = [
      /(Android)(.+)(Mobile)/i,
      /BlackBerry/i,
      /iPhone|iPod/i,
      /Opera Mini/i,
      /IEMobile/i,
    ];
    return regexs.some((b) => userAgent.match(b));
  }, []);
  const tab = params.get("tab");

  const [showLessonView, setShowLessonView] = useState(false);
  const [showLessonPreView, setShowLessonPreView] = useState(false);
  const [showInstructionModal, setShowInstructionModal] = useState(false);
  const [lastPageNum, setLastPageNum] = useState(1);
  const [isMaterials, setIsMaterials] = useState(false);
  const [resumeModal, setResumeModal] = useState(false);
  const [backHover, setBackHover] = useState(false);
  const [showResumeMenu, setShowResumeMenu] = useState(false);
  const [startResume, setStartResume] = useState(false);

  const [showQuizPreview, setShowQuizPreview] = useState(false);
  const [showGradeMenu, setShowGradeMenu] = useState(false);

  const [shareLesson, setShareLesson] = useState(false);
  const [showLessonFeedback, setShowLessonFeedback] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [openShareModal, setOpenShareModal] = useState(false);

  const [isRefetching, setIsRefetching] = useState(false);
  /** Skip open tracking for refetch / close refresh; reset when modal opens a lesson. */
  const skipOpenTrackRef = useRef(false);
  const openTrackedLessonIdRef = useRef<string | null>(null);
  /** Resource (type 5): complete once per modal open when the PDF reader opens. */
  const resourcePdfCompleteForLessonIdRef = useRef<string | null>(null);
  const [showMaterail, setShowMaterial] = useState(false);
  const [needPrint, setNeedPrint] = useState(false);

  const [showStudentLinkModal, setShowStudentLinkModal] = useState(false);
  const { setIsPreview: setContextPreview } = usePresentationContext();

  const [objectAnswers, setObjectAnswers] = useState<string[]>([]);

  const { successToast } = useToast();
  const [user] = useUser();

  const { trackEvent } = useAnalytics();
  const { getFormId, setFormId, removeFormId } = useExitTicketLocalStorage();
  let countDownTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    content,
    setContent,
    setLesson,
    setIsPreview,
    setCourse,
    setIsPreProgramQuiz,
    setQuizModules,
    grade,
    setGrade,
    exitTicketActive,
    setExitTicketActive,
    setIsNewLessonLayout,
    setIsPlatformTower,
    setMcId,
    setMcName,
    setLaunchedGrade,
    setCollectionId,
    setCollectionName,
    setProgramName,
    setProgramId,
    setExitPresentationHandler,
  } = useStandaloneContent();

  const [generatePrettyUrl, { loading: generating }] =
    useGenerateParentLinkForUserMutation();
  const programOptions = useQueryDictionary({ dictionary: "Programs" });

  const [sllGetLessonInfo, { data, loading, refetch }] =
    useSllGetLessonInfoLazyQuery({
      fetchPolicy: "network-only",
      onCompleted(data) {
        if (data.sllGetLessonInfo) {
          if (isPlatformTower) {
            setLesson(data.sllGetLessonInfo);
            const sortModules = [
              ...(data.sllGetLessonInfo.presentation ?? []),
            ].sort(sortByPosition);
            setContent(sortModules?.at(0) as SllModule);
            setIsPreview(true);
            setLastPageNum(1);
            setShowLessonPreView(true);
            setIsPlatformTower(true);
          }
          if (
            openTrackEvent &&
            !skipOpenTrackRef.current &&
            openTrackedLessonIdRef.current !== data.sllGetLessonInfo.id
          ) {
            const l = data.sllGetLessonInfo;
            openTrackedLessonIdRef.current = l.id;
            const isCaregiverContext =
              !!isParentApp ||
              pathname.includes("/caregiver") ||
              pathname.includes("/parent/dashboard");
            const isGettingStartedContext =
              pathname.includes("/getting-started");
            trackEvent(openTrackEvent, {
              lesson_name: l.name,
              course_name: course?.name,
              grade: resolveMixpanelStandaloneGrade({
                ageGroup: l.ageGroup,
                gradeLevel: l.gradeLevel,
              }),
              ...(isCaregiverContext && { isCaregiver: true }),
              ...(isGettingStartedContext && { isGettingStarted: true }),
              ...mixpanelSkillProps({
                categories: l.categories,
                skill: l.skill,
              }),
              ...mixpanelMoodProps({ categories: l.categories }),
              schoolId: user?.schools?.[0]?.id,
              schoolName: user?.schools?.[0]?.name,
              userId: user?.id,
              userName: `${user?.firstName} ${user?.lastName}`,
              lessonType: resolveStandaloneMixpanelLessonType({
                lesson: { uniqueName: l.uniqueName, lessonType: l.lessonType },
              }),
              type: "Standalone",
              ...(l.collections &&
                l.collections.length && {
                  collection_name: l.collections.filter(
                    (c) => c?.collectionType === "Theme",
                  )[0]?.name,
                }),
              program_name: resolveMixpanelProgramName({
                lessonType: l.lessonType,
                isCaregiver: isCaregiverContext,
                programType: l.multiCollections?.[0]?.programType,
                programName: programName,
                multiCollectionName: l.multiCollections?.[0]?.name ?? mcName,
                courseName: course?.name,
              }),
              ...mixpanelMultiCollectionProps({
                multiCollections: l.multiCollections,
                mcName,
              }),
              ...mixpanelCompetencyProps(l.competency),
            });
          }
          skipOpenTrackRef.current = false;
        }
      },
    });

  const handleRefetch = useCallback(async () => {
    setIsRefetching(true);
    skipOpenTrackRef.current = true;
    try {
      await refetch();
    } finally {
      setIsRefetching(false);
    }
  }, [refetch]);

  const [
    getLastVisitedModule,
    { data: lastVisitedModule, loading: visitloading },
  ] = useSllGetLastVisitedModuleLazyQuery({ fetchPolicy: "network-only" });
  const [getAusPreProgramInfo, { data: data1, loading: loading1 }] =
    useSllGetPreProgramInfoLazyQuery({ fetchPolicy: "network-only" });
  const [getPreProgramQuiz, { data: data2, loading: loading2 }] =
    useSllPreProgramQuizLazyQuery({
      fetchPolicy: "network-only",
      onCompleted(data) {
        if (data.sllPreProgramQuiz) {
          setIsPreProgramQuiz(true);
          setQuizModules((data.sllPreProgramQuiz as SllModule[]) ?? []);
        }
      },
    });
  const [getFeedbackForm, { loading: feedbackLoading }] =
    useFeedbackFormLazyQuery({
      fetchPolicy: "network-only",
      onCompleted(res) {
        const currentTime = new Date().getTime();
        const expiration = parseInt(res.feedbackForm?.expirationAt ?? "0");
        const url = createURL(res.feedbackForm!.link ?? "");
        const ticketCount = res.feedbackForm?.exitTicketCount ?? 0;
        const expiryMins = (expiration - currentTime) / 1000;

        setExitTicketActive({
          lessonNum: 1,
          formData: res.feedbackForm?.formData,
          status: true,
          type: res.feedbackForm?.type ?? undefined,
          exitTicketCount: ticketCount,
          expiryMinutes: expiryMins,
          copyLink: url,
          code: res.feedbackForm?.link ?? "",
        });
      },
    });

  const [cancelFeedbackForm, { loading: canceling }] =
    useCancelFeedbackFormMutation({
      onError(e) {
        logger.debug(`[e]`, e);
      },
      onCompleted(data) {
        if (data.cancelFeedbackForm) {
          removeFormId(lessonInfo?.id);
          setExitTicketActive({
            lessonNum: 1,
            formData: exitTicketActive?.formData,
            status: false,
            type: exitTicketActive?.type,
            exitTicketCount: 0,
            expiryMinutes: 0,
            copyLink: "",
            code: "",
          });
        }
      },
    });

  const onMarkLessonCompleted = useCallback(() => {
    handleRefetch();
    successToast("This lesson has been successfully marked as complete.");
  }, [handleRefetch, successToast]);

  const {
    sllMarkLessonComplete,
    sllLessonNotStarted,
    loading: loadingLessonComplete,
  } = useMarkLessonComplete(onMarkLessonCompleted, undefined);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (lessonId) {
      openTrackedLessonIdRef.current = null;
      skipOpenTrackRef.current = false;
      sllGetLessonInfo({
        variables: {
          input: { id: lessonId },
        },
      });
    } else if (isAusPreProgramQuiz) {
      getAusPreProgramInfo();
    }
  }, [
    open,
    lessonId,
    isAusPreProgramQuiz,
    sllGetLessonInfo,
    getAusPreProgramInfo,
  ]);

  useEffect(() => {
    if (grade && isAusPreProgramQuiz) {
      const gradeId = programOptions.find((program) =>
        program.label.includes(grade.label),
      )?.value;
      if (gradeId) {
        getPreProgramQuiz({
          variables: {
            input: { id: gradeId },
          },
        });
      }
    }
  }, [grade, programOptions, isAusPreProgramQuiz, getPreProgramQuiz]);

  useEffect(() => {
    if (gradeName && isAusPreProgramQuiz) {
      const program = auProgramGradeLevels.find((program) =>
        program.value.includes(gradeName),
      );
      if (program) {
        setGrade(program);
      }
    }
    if (gradeName) {
      setGrade({
        label: gradeName,
        value: gradeName,
      });
    }
  }, [programOptions, isAusPreProgramQuiz, gradeName, setGrade]);

  useEffect(() => {
    if (programId) {
      setProgramId(programId);
    }
    if (programName) {
      setProgramName(programName);
    }
    if (mcId) {
      setMcId(mcId);
    }
    if (mcName) {
      setMcName(mcName);
    }
    if (launchedGrade) {
      setLaunchedGrade(launchedGrade);
    }
    if (collectionId) {
      setCollectionId(collectionId);
    }
    if (collectionName) {
      setCollectionName(collectionName);
    }
  }, [
    programId,
    programName,
    mcId,
    mcName,
    launchedGrade,
    collectionId,
    collectionName,
    setProgramId,
    setProgramName,
    setMcId,
    setMcName,
    setLaunchedGrade,
    setCollectionId,
    setCollectionName,
  ]);

  useEffect(() => {
    if (lessonId && !isParentApp) {
      getLastVisitedModule({
        variables: {
          input: { id: lessonId },
        },
      });
    }
  }, [lessonId, getLastVisitedModule, isParentApp]);

  const backTitle = useMemo(() => {
    if (pathname === "/dashboard") {
      return "Return to Dashboard";
    } else if (pathname.includes("/programs")) {
      return "Return to Programs";
    } else if (pathname === "/wellbeingfoundations") {
      if (tab === "student-introduction") {
        return "Return to Student Introduction";
      } else if (tab === "brainbreaks") {
        return "Return to Brain Breaks";
      } else if (tab === "skillbuilders") {
        return "Return to Skill-Builders";
      } else {
        return "Return to Well-Being Foundations";
      }
    } else if (pathname === "/lessons") {
      return "Return to Lesson Library";
    } else if (pathname === "/lessons/studentvoiceseries") {
      return "Return to Student Voice Series";
    } else if (pathname === "/lessons/transitions") {
      return "Return to Transitions™";
    } else {
      return "Go Back";
    }
  }, [pathname, tab]);

  const lessonInfo = useMemo(() => {
    if (data?.sllGetLessonInfo) {
      return data?.sllGetLessonInfo;
    } else if (data1?.sllGetPreProgramInfo) {
      return data1.sllGetPreProgramInfo;
    }
  }, [data?.sllGetLessonInfo, data1?.sllGetPreProgramInfo]);

  const newLessonLayout = useMemo(
    () => lessonInfo?.isEducatorCaregiverLessonLayout,
    [lessonInfo],
  );

  const hasLessonPresentationContents = useMemo(
    () => (lessonInfo?.presentation ?? []).length > 0 || isAusPreProgramQuiz,
    [isAusPreProgramQuiz, lessonInfo?.presentation],
  );

  const isCaregiver = lessonInfo?.lessonType === "7";

  useEffect(() => {
    if (exitTicketActive && exitTicketActive?.expiryMinutes > 0) {
      countDownTimer.current = setInterval(() => {
        const expiryMinutes = exitTicketActive?.expiryMinutes - 60;
        const status = exitTicketActive.expiryMinutes - 60 >= 0 ? true : false;

        setExitTicketActive({
          ...exitTicketActive,
          status: status,
          expiryMinutes: expiryMinutes,
        });
      }, 60 * 1000);
    }
    return () => {
      if (countDownTimer.current) {
        clearInterval(countDownTimer.current);
        countDownTimer.current = null;
      }
    };
  }, [exitTicketActive, setExitTicketActive]);

  const formId = useMemo(() => {
    return getFormId(lessonInfo?.id);
  }, [lessonInfo?.id, getFormId]);

  useEffect(() => {
    if (formId) {
      getFeedbackForm({
        variables: {
          input: { id: formId },
        },
      });
    }
  }, [formId, getFeedbackForm]);

  useEffect(() => {
    if (newLessonLayout) {
      setIsNewLessonLayout(true);
    } else {
      setIsNewLessonLayout(false);
    }
  }, [newLessonLayout, setIsNewLessonLayout]);

  useEffect(() => {
    if (lessonInfo?.overviewAnswers2) {
      setObjectAnswers(JSON.parse(lessonInfo.overviewAnswers2 || "[]"));
    }
  }, [lessonInfo?.overviewAnswers2]);

  useEffect(() => {
    if (lessonInfo?.presentation) {
      const formContent = lessonInfo?.presentation.find(
        (module) => module?.type === ContentType.FORM,
      );
      if (formContent && formContent.formData) {
        const jsonFormData = JSON.parse(formContent.formData) as FeedbackForm;
        if (jsonFormData && !exitTicketActive) {
          setExitTicketActive({
            lessonNum: 1,
            formData: formContent.formData,
            status: false,
            type: Number(jsonFormData?.surveyType),
            exitTicketCount: 0,
            expiryMinutes: 0,
            copyLink: "",
            code: "",
          });
        }
      }
    }
  }, [lessonInfo, exitTicketActive, setExitTicketActive]);

  const handleOpenShareModal = useCallback(() => {
    // videoPlayerRef.current?.setMute(true);
    if (
      lessonInfo?.presentation &&
      lessonInfo.presentation.length > 0 &&
      !isCaregiver
    ) {
      setOpenShareModal(true);
    } else {
      setShareLesson(true);
    }
  }, [lessonInfo, isCaregiver]);

  const handleCompleteLesson = useCallback(async () => {
    if (lessonInfo && !loadingLessonComplete && !isRefetching) {
      await sllMarkLessonComplete(lessonInfo, programName);
    }
    // Keep materials modal open so Undo snackbar can show for material-only flow
  }, [
    lessonInfo,
    sllMarkLessonComplete,
    isRefetching,
    loadingLessonComplete,
    programName,
  ]);

  const handleUndoCompleteLesson = useCallback(async () => {
    if (lessonInfo?.id && !loadingLessonComplete && !isRefetching) {
      await sllLessonNotStarted(lessonInfo.id);
      await handleRefetch();
    }
  }, [
    lessonInfo?.id,
    sllLessonNotStarted,
    loadingLessonComplete,
    isRefetching,
    handleRefetch,
  ]);

  const handleShareTeacher = () => {
    setOpenShareModal(false);
    setShareLesson(true);
  };

  const handleShareStudent = () => {
    setShowStudentLinkModal(true);
    setOpenShareModal(false);
  };

  // const videoPlayerRef = useRef<{setMute: (muted: boolean) => void}>(null);

  const materialsCloseHandlerRef = useRef<(() => Promise<void>) | null>(null);
  const bindMaterialsCloseHandler = useCallback(
    (handler: (() => Promise<void>) | null) => {
      materialsCloseHandlerRef.current = handler;
    },
    [],
  );

  const handleClose = async (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    // Parent lesson X / backdrop: flush pending materials complete first
    if (showInstructionModal && materialsCloseHandlerRef.current) {
      await materialsCloseHandlerRef.current();
    }
    onClose();
    setContextPreview(false);
  };

  const handleCloseMaterialView = () => {
    if (lessonInfo?.lessonType === "5") onClose();
    setShowMaterial(false);
    setIsPreview(false);
    setContextPreview(false);
    setContent(undefined);
  };

  const openRecommendedLesson = useCallback(
    (lesson: SllLesson) => {
      if (pathname.startsWith("/standalone/lessons") && tab) {
        history.push(`/standalone/lessons?tab=${tab}&lid=${lesson.id}`);
      } else {
        history.push(`/standalone/lessons?lid=${lesson.id}`);
      }
    },
    [history, pathname, tab],
  );

  const handleLessonPreview = () => {
    // videoPlayerRef.current?.setMute(true);
    if (isAusPreProgramQuiz) {
      const sortModules = [...(data2?.sllPreProgramQuiz ?? [])].sort(
        sortByAlphabetic,
      );
      setLesson(lessonInfo);
      setQuizModules((sortModules as SllModule[]) ?? []);
      setContent(sortModules?.at(0) as SllModule);
      setIsPreview(true);
      setLastPageNum(1);
      setShowLessonPreView(false);
      setShowQuizPreview(true);
      setIsMaterials(false);
      setIsPreProgramQuiz(true);
      return;
    }
    const sortModules = [...(lessonInfo?.presentation ?? [])].sort(
      sortByPosition,
    );
    setContent(sortModules?.at(0) as SllModule);
    setLesson(lessonInfo);
    setCourse(course);
    setIsPreview(true);
    setLastPageNum(1);
    setShowLessonPreView(true);
    setIsMaterials(false);
    setContextPreview(true);
  };

  const handleBeginLesson = useCallback(() => {
    // videoPlayerRef.current?.setMute(true);
    setShowResumeMenu(false);
    setShowGradeMenu(false);
    const materialLesson = lessonInfo?.materials?.find((material) =>
      material?.name.toLowerCase().includes("student"),
    );
    if (materialLesson && !showInstructionModal) {
      setShowInstructionModal(true);
      return;
    }
    setShowInstructionModal(false);
    if (showLessonPreView) {
      setShowLessonPreView(false);
    }
    const sortModules = [...(lessonInfo?.presentation ?? [])].sort(
      sortByPosition,
    );
    if (
      lastVisitedModule?.sllGetLastVisitedModule &&
      sortModules &&
      startResume
    ) {
      const index = sortModules?.findIndex(
        (m) => m?.id === lastVisitedModule?.sllGetLastVisitedModule?.moduleId,
      );
      if (index > -1) {
        setContent(sortModules?.at(index) as SllModule);
        setLastPageNum(
          lastVisitedModule?.sllGetLastVisitedModule.pageNumber ?? 1,
        );
      } else {
        setContent(sortModules?.at(0) as SllModule);
        setLastPageNum(1);
      }
    } else {
      setContent(sortModules?.at(0) as SllModule);
      setLastPageNum(1);
    }
    setLesson(lessonInfo);
    setCourse(course);
    setIsPreview(false);
    setContextPreview(false);
    setShowLessonPreView(false);
    setShowLessonView(true);
  }, [
    course,
    startResume,
    lastVisitedModule?.sllGetLastVisitedModule,
    lessonInfo,
    showInstructionModal,
    showLessonPreView,
    setContent,
    setCourse,
    setIsPreview,
    setLesson,
    setShowInstructionModal,
  ]);

  const handleStartLesson = useCallback(() => {
    // videoPlayerRef.current?.setMute(true);
    setShowResumeMenu(false);
    setShowGradeMenu(false);
    const materialLesson = lessonInfo?.materials?.find((material) =>
      material?.name.toLowerCase().includes("student"),
    );
    if (materialLesson && !showInstructionModal) {
      setShowInstructionModal(true);
      return;
    }
    setShowInstructionModal(false);
    setShowLessonPreView(false);
    setIsPreview(false);
    const sortModules = [...(lessonInfo?.presentation ?? [])].sort(
      sortByPosition,
    );
    setContent(sortModules?.at(0) as SllModule);
    setLastPageNum(1);
    setShowLessonView(true);
  }, [
    lessonInfo,
    showInstructionModal,
    setContent,
    setIsPreview,
    setShowInstructionModal,
  ]);

  const handleCloseLesson = useCallback(() => {
    exitDocumentFullscreen();
    setIsPreview(false);
    setShowLessonView(false);
    setShowLessonPreView(false);
    setShowQuizPreview(false);
    setContextPreview(false);
    if (lessonId && !isParentApp) {
      getLastVisitedModule({
        variables: {
          input: { id: lessonId },
        },
      });
    }
    if (lessonId) {
      skipOpenTrackRef.current = true;
      sllGetLessonInfo({
        variables: {
          input: { id: lessonId },
        },
      });
    }
  }, [
    lessonId,
    isParentApp,
    sllGetLessonInfo,
    setIsPreview,
    getLastVisitedModule,
    setContextPreview,
  ]);

  // Let Mark Complete (from End Lesson modal) close presentation without depending on goBack props.
  const handleCloseLessonRef = useRef(handleCloseLesson);
  handleCloseLessonRef.current = handleCloseLesson;
  useEffect(() => {
    setExitPresentationHandler(() => {
      handleCloseLessonRef.current();
    });
    return () => setExitPresentationHandler(null);
  }, [setExitPresentationHandler]);

  const handleCloseLessonPreview = useCallback(() => {
    if (isPlatformTower || lessonInfo?.lessonType === "5") onClose();
    setShowLessonPreView(false);
    setShowQuizPreview(false);
    setContextPreview(false);

    if (newLessonLayout) {
      handleCloseLesson();
    }
  }, [
    isPlatformTower,
    lessonInfo?.lessonType,
    onClose,
    setContextPreview,
    newLessonLayout,
    handleCloseLesson,
  ]);

  const handleCloseResumeModal = useCallback(() => {
    setResumeModal(false);
  }, []);

  const handleResumeLesson = useCallback(() => {
    setShowResumeMenu(false);
    setShowGradeMenu(false);
    setStartResume(true);
    const materialLesson = lessonInfo?.materials?.find((material) =>
      material?.name.toLowerCase().includes("student"),
    );
    if (materialLesson && !showInstructionModal) {
      setShowInstructionModal(true);
      return;
    }
    setShowInstructionModal(false);
    if (showLessonPreView) {
      setShowLessonPreView(false);
    }
    const sortModules = [...(lessonInfo?.presentation ?? [])].sort(
      sortByPosition,
    );
    if (lastVisitedModule?.sllGetLastVisitedModule && sortModules) {
      const index = sortModules?.findIndex(
        (m) => m?.id === lastVisitedModule?.sllGetLastVisitedModule?.moduleId,
      );
      if (index > -1) {
        setContent(sortModules?.at(index) as SllModule);
        setLastPageNum(
          lastVisitedModule?.sllGetLastVisitedModule.pageNumber ?? 1,
        );
      } else {
        setContent(sortModules?.at(0) as SllModule);
        setLastPageNum(1);
      }
    } else {
      setContent(sortModules?.at(0) as SllModule);
      setLastPageNum(1);
    }
    setResumeModal(false);
    setLesson(lessonInfo);
    setCourse(course);
    setIsPreview(false);
    setContextPreview(false);
    setShowLessonPreView(false);
    setShowLessonView(true);
  }, [
    lessonInfo,
    showInstructionModal,
    showLessonPreView,
    lastVisitedModule?.sllGetLastVisitedModule,
    setLesson,
    setCourse,
    course,
    setIsPreview,
    setContextPreview,
    setContent,
  ]);

  const handleStartBegin = useCallback(() => {
    // videoPlayerRef.current?.setMute(true);
    if (isAusPreProgramQuiz) {
      const sortModules = [...(data2?.sllPreProgramQuiz ?? [])]?.sort(
        sortByAlphabetic,
      );
      setQuizModules((sortModules as SllModule[]) ?? []);
      setContent(sortModules?.at(0) as SllModule);
      setLesson(lessonInfo);
      setIsPreview(false);
      setLastPageNum(1);
      setShowLessonPreView(false);
      setShowQuizPreview(false);
      setIsMaterials(false);
      setIsPreProgramQuiz(true);
      setShowLessonView(true);
      return;
    }
    setShowResumeMenu(false);
    setShowGradeMenu(false);
    setStartResume(false);

    if (
      !showInstructionModal &&
      lessonInfo?.materials &&
      lessonInfo?.materials?.length > 0
    ) {
      setShowInstructionModal(true);
      return;
    }
    setShowInstructionModal(false);
    const sortModules = [...(lessonInfo?.presentation ?? [])].sort(
      sortByPosition,
    );
    setContent(sortModules?.at(0) as SllModule);
    setLastPageNum(1);
    setResumeModal(false);
    setLesson(lessonInfo);
    setCourse(course);
    setIsPreview(false);
    setShowQuizPreview(false);
    setContextPreview(false);
    setShowLessonPreView(false);
    setShowLessonView(true);
  }, [
    isAusPreProgramQuiz,
    lessonInfo,
    showInstructionModal,
    setContent,
    setLesson,
    setCourse,
    course,
    setIsPreview,
    data2?.sllPreProgramQuiz,
    setQuizModules,
    setIsPreProgramQuiz,
    setContextPreview,
  ]);

  const handleClickStudyPlan = useCallback(
    (collection: SllCourse) => () => {
      if (gotoCollection) {
        gotoCollection(collection);
      }
    },
    [gotoCollection],
  );

  const priorLessons = useMemo(() => {
    const lessonIds: string[] = JSON.parse(
      lessonInfo?.recommendedPrior || "[]",
    );
    return lessonlist?.filter((lesson) => lessonIds.includes(lesson.id)) ?? [];
  }, [lessonlist, lessonInfo?.recommendedPrior]);

  const nextLessons = useMemo(() => {
    const lessonIds: string[] = JSON.parse(lessonInfo?.recommendedNext || "[]");
    return lessonlist?.filter((lesson) => lessonIds.includes(lesson.id)) ?? [];
  }, [lessonlist, lessonInfo?.recommendedNext]);

  const completedPercent = useMemo(() => {
    let percent = lessonInfo?.progress ? lessonInfo.progress : 0;
    return percent;
  }, [lessonInfo]);

  const lessonSteps = useMemo(() => {
    return (
      lessonInfo?.presentation?.filter(
        (l) => l?.name !== "Lesson Complete" || l?.lastInLesson !== true,
      ) ?? []
    );
  }, [lessonInfo]);

  const handleResumeDropdownVisible = useCallback((visible: boolean) => {
    setShowResumeMenu(visible);
  }, []);

  const handleYearDropdownVisible = useCallback((visible: boolean) => {
    setShowGradeMenu(visible);
  }, []);

  const handleSelectGrade: MenuProps["onClick"] = ({ key }) => {
    setShowGradeMenu(false);
    setGrade(auProgramGradeLevels[key]);
  };

  const handleScroll = useCallback(() => {
    setShowGradeMenu(false);
  }, []);

  const handleOpenFeedback = (e: any) => {
    if (showInstructionModal) {
      setShowInstructionModal(false);
    }
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    setShowLessonFeedback(true);
  };

  const handleCloseLessonFeedback = useCallback(() => {
    setShowLessonFeedback(false);
  }, []);

  const handleCopyToClipboard = useCallback(async () => {
    if (isCaregiver) {
      const { data } = await generatePrettyUrl({
        variables: { input: { uniqueName: lessonInfo?.uniqueName ?? "" } },
      });
      if (data?.generateParentLinkForUser) {
        const { id, resource } = data.generateParentLinkForUser;
        if (id && resource) {
          await navigator.clipboard.writeText(
            `${window.location.protocol}//${window.location.host}/parent/login?id=${id}&resource=${resource}`,
          );
          successToast("Link Copied Successfully!");
        }
      }
    } else {
      successToast("Link Copied Successfully!");
    }
    setShareLesson(false);
  }, [generatePrettyUrl, successToast, isCaregiver, lessonInfo?.uniqueName]);

  const generateExitTicket = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    setShowExitModal(true);
  };

  const handleActiveFormPreview = useCallback(
    async (
      feedback: FeedbackFormContent,
      expiration: any,
      ticketCount?: any,
    ) => {
      const url = createURL(feedback.link ?? "");
      setFormId(lessonInfo?.id, feedback.id);
      const ticket = {
        ...exitTicketActive,
        lessonNum: 1,
        status: true,
        exitTicketCount: ticketCount,
        expiryMinutes: expiration * 60,
        copyLink: url,
        code: feedback.link ?? "",
      };
      setExitTicketActive(ticket);
      await window.navigator.clipboard.writeText(url);
      successToast(
        "Exit Ticket Link Successfully Created and Link copied successfully!",
      );
    },
    [
      successToast,
      exitTicketActive,
      setExitTicketActive,
      lessonInfo?.id,
      setFormId,
    ],
  );

  const cancelExitTicket = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }

    const formId = getFormId(lessonInfo?.id);
    if (formId) {
      cancelFeedbackForm({
        variables: {
          input: { id: formId },
        },
      });
    }
  };

  const handleActiveStudentLink = useCallback(
    async (feedback: AbsentStudentLesson) => {
      const url = createStudentLink(feedback.link ?? "", true);
      await window.navigator.clipboard.writeText(url);
      successToast(
        "Link Created Successfully. Please share this link with your student(s).",
      );
    },
    [successToast],
  );
  //Show Material Content
  const handleShowMaterial = useCallback(
    (content: SllModule, lesson?: SllLesson, printable?: boolean) => {
      setContent(content);
      setNeedPrint(printable ?? false);
      setShowInstructionModal(false);
      setShowLessonPreView(false);
      setIsPreview(true);
      setShowMaterial(true);
    },
    [setContent, setIsPreview],
  );

  useEffect(() => {
    resourcePdfCompleteForLessonIdRef.current = null;
  }, [lessonId]);

  useEffect(() => {
    if (!lessonInfo || lessonInfo.id !== lessonId) return;
    if (lessonInfo.lessonType !== "5") return;

    const pdfContent = lessonInfo.materials?.find(
      (m) => m?.type === ContentType.PDF,
    );
    if (!pdfContent) return;

    handleShowMaterial(pdfContent, lessonInfo, false);

    const alreadyComplete =
      lessonInfo.status === LessonStatus.Completed ||
      lessonInfo.progress === 100;
    if (alreadyComplete) return;
    if (resourcePdfCompleteForLessonIdRef.current === lessonInfo.id) return;

    resourcePdfCompleteForLessonIdRef.current = lessonInfo.id;
    void handleCompleteLesson();
  }, [lessonId, lessonInfo, handleShowMaterial, handleCompleteLesson]);

  const gradeMenu = useMemo(() => {
    const items: MenuProps["items"] = auProgramGradeLevels.map(
      (gradeLevel, index) => {
        return {
          key: index,
          label: (
            <div
              className={`${styles.gradeDropDownMenuitem} ${grade?.label === gradeLevel.label ? styles.select : ""}`}
            >
              {gradeLevel.label}
            </div>
          ),
        };
      },
    );
    return items;
  }, [grade]);

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div
          className={styles.dropDownResumnMenuitem}
          onClick={handleResumeLesson}
        >
          <div className={styles.resumeMenuItem}>
            <div className={styles.resumeProgressBackBar}>
              <div
                className={styles.resumeProgressBar}
                style={{
                  width: `${completedPercent}%`,
                  backgroundColor:
                    completedPercent === 100 ? "#62C590" : "#62C590",
                  borderBottomRightRadius:
                    completedPercent === 100 ? "3px" : "0",
                }}
              ></div>
              <span className={styles.inprogressText}>Lesson in Progress</span>
            </div>
            <div className={styles.resumeText}>Resume Lesson</div>
          </div>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div className={styles.dropDownMenuitem} onClick={handleStartBegin}>
          Start from Beginning
        </div>
      ),
    },
  ];

  const normalMenu: MenuProps["items"] = [
    {
      key: "3",
      label: (
        <div className={styles.dropDownMenuitem}>{"Rate This Lesson"}</div>
      ),
      onClick: (e) => handleOpenFeedback(e),
    },
  ];

  const ticketGenerateMenu: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div className={styles.dropDownMenuitem}>{"Generate Exit Ticket"}</div>
      ),
      onClick: (e) => generateExitTicket(e),
    },
    {
      key: "3",
      label: (
        <div className={styles.dropDownMenuitem}>{"Rate This Lesson"}</div>
      ),
      onClick: (e) => handleOpenFeedback(e),
    },
  ];
  const copyToClipboard = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
    successToast("Link Copied Successfully!");
  };

  const ticketActiveMenu: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <div
          className={styles.activeInfoMenu}
          onClick={(e) => e.stopPropagation()}
        >
          <OutlineIcon width={21} className={styles.warningIcon} />
          <div className={styles.activeInfo}>
            <span>
              <strong>
                {Math.round((exitTicketActive?.expiryMinutes ?? 0) / 60)}
              </strong>{" "}
              Minutes Left
            </span>
            <span>
              <strong>{exitTicketActive?.exitTicketCount}</strong> Responses Max
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "2",
      label: <div className={styles.dropDownMenuitem}>Cancel</div>,
      onClick: (e) => cancelExitTicket(e),
    },
    {
      key: "3",
      label: (
        <CopyToClipboard text={exitTicketActive?.copyLink ?? ""}>
          <div className={styles.dropDownMenuitem}>Copy Link</div>
        </CopyToClipboard>
      ),
      onClick: (e) => copyToClipboard(e),
    },
    {
      key: "4",
      label: <div className={styles.dropDownMenuitem}>Regenerate</div>,
      onClick: (e) => generateExitTicket(e),
    },
  ];

  const activeSurveyTitle = (type: Number) => {
    switch (type) {
      case SURVEY_TYPE.RESEARCH: {
        return "Research Survey Active";
      }
      case SURVEY_TYPE.NA_PRE_PROGRAM_QUIZ: {
        return "Pre-Program Active";
      }
      case SURVEY_TYPE.EXIT_TICKET: {
        return "Exit Ticket Active";
      }
      case SURVEY_TYPE.NA_POST_PROGRAM_QUIZ: {
        return "Post-Program Active";
      }
      default: {
        return "Research Survey Active";
      }
    }
  };

  const handleClickActiveMenu = (e: any) => {
    if (e.domEvent) {
      e.domEvent.stopPropagation();
    } else {
      e.stopPropagation();
    }
  };

  const isSurveyModuleOnly = useMemo(
    () =>
      lessonInfo?.presentation?.length === 1 &&
      lessonInfo?.presentation?.every(
        (item) => item?.type === ContentType.FORM,
      ),
    [lessonInfo?.presentation],
  );

  const ticketActivatedMenu: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <Dropdown
          menu={{ items: ticketActiveMenu }}
          trigger={["hover"]}
          placement="top"
        >
          <div className={styles.exitTicketActiveButton}>
            <TicketIcon />
            {exitTicketActive?.type
              ? activeSurveyTitle(exitTicketActive?.type)
              : ""}
          </div>
        </Dropdown>
      ),
      onClick: (e) => handleClickActiveMenu(e),
    },
    {
      key: "2",
      label: <div className={styles.dropDownMenuitem}>{"Rate Lesson"}</div>,
      onClick: (e) => handleOpenFeedback(e),
    },
  ];

  const burgeritems: MenuProps["items"] = [
    {
      label: "",
      key: "SubMenu",
      icon: (
        <Button
          label=""
          size="small"
          className={styles.burgerButton}
          onClick={(e) => e.stopPropagation()}
          leftIcon={
            feedbackLoading || canceling ? (
              <Spinner width={25} height={25} />
            ) : (
              <BurgerIcon width={26} height={25} />
            )
          }
        />
      ),
      children: exitTicketActive
        ? !!exitTicketActive.status
          ? ticketActivatedMenu
          : ticketGenerateMenu
        : normalMenu,
    },
  ];

  const suggestBar = (
    <div className={styles.suggestView}>
      {lessonInfo?.lessonType &&
        ["0", "1", "2", "3", "4", "5", "6", "7"].indexOf(
          lessonInfo?.lessonType,
        ) !== -1 && (
          <div className={styles.lessonsView}>
            <LordIcon
              src={IconLesson}
              isHover={true}
              colors={{ primary: "#FFF", secondary: "#FFF" }}
              size={24}
            />
            {
              <div
                className={`${styles.lessonsText} ${lessonInfo?.lessonType === "1" ? styles.narrowText : ""}`}
              >
                {lessonTypeLabels[lessonInfo?.lessonType]}
              </div>
            }
          </div>
        )}
      {(lessonInfo?.country === countryOptions[1].label &&
        lessonInfo.suggestedDuration &&
        Number(lessonInfo.suggestedDuration) > 0) ||
      (lessonInfo?.country !== countryOptions[1].label &&
        lessonInfo?.duration &&
        lessonInfo?.duration > 0) ? (
        <div
          className={`${styles.durationView} ${lessonInfo?.lessonType && ["0", "1", "2", "3", "4"].indexOf(lessonInfo?.lessonType) !== -1 ? "" : styles.durationViewAlone}`}
        >
          <ClockCircleOutlined className={styles.clockIcon} />
          <div className={styles.durationText}>
            {lessonInfo?.country === countryOptions[1].label
              ? lessonInfo.suggestedDuration
              : lessonInfo?.duration}
            &nbsp;<span>min</span>
          </div>
        </div>
      ) : null}
    </div>
  );

  const requiredMaterial = useMemo(() => {
    const required = lessonInfo?.materials?.filter((material) =>
      material?.isRequired ? true : false,
    );
    return required && required?.length > 0 ? true : false;
  }, [lessonInfo?.materials]);

  const showCheatSheet = shouldShowLessonMaterialsCheatSheet(
    newLessonLayout,
    requiredMaterial,
  );

  const isLessonOverlayActive =
    showLessonView || showLessonPreView || showQuizPreview || showMaterail;

  return (
    <LessonModal
      open={open}
      onClose={handleClose}
      onScroll={handleScroll}
      closeButton={isMobile ? false : true}
      className={isLessonOverlayActive ? styles.modalPresenting : ""}
    >
      {loading || loading1 || visitloading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : (
        <div
          className={styles.wrapper}
          style={{
            paddingTop: isMobile ? "0" : "62px",
            position: "relative",
          }}
        >
          <div className={styles.backdrop} onClick={handleClose} />
          {isMobile && (
            <div className={styles.mobileBackButtonView}>
              <div className={styles.mobileBackButton} onClick={handleClose}>
                <FSBackIcon width={26} height={26} />
              </div>
              <span className={styles.backText}>Back to Dashboard</span>
            </div>
          )}
          <div id="coverView" className={styles.coverView}>
            <div className={styles.reelTitleView}>
              <div className={styles.lessonBannerTitle}>{lessonInfo?.name}</div>
              {/* <div className={styles.gradeLevel}>
              <LordIcon src={IconGrade} isHover={true} colors={{primary: '#FFF', secondary: '#FFF'}}
                size={20} target='.LessonProgressModal_gradeLevel__m3z75'/>
              <span>{lessonInfo?.gradeLevel}</span>
            </div> */}
            </div>

            {!!lessonInfo?.uniqueName && (
              <div className={styles.shareIcon}>
                <LinkShareIcon
                  uniqueName={lessonInfo.uniqueName}
                  handleClickShare={handleOpenShareModal}
                />
              </div>
            )}
            <div className={styles.imageSection}>
              <div className={styles.coverImage}>
                <div className={styles.reelImgOverlay}></div>
                {lessonInfo?.coverImage ? (
                  <img
                    src={getMediaAbsolutePath(lessonInfo.coverImage)}
                    alt="OP Reel Cover"
                    className={styles.reelImg}
                  />
                ) : null}
              </div>
              <div className={styles.objWrapper}>
                <div className={styles.objContentView}>
                  <div className={styles.objHeader}>{"Lesson Objectives:"}</div>
                  <ul className={styles.boxView}>
                    {(objectAnswers ?? []).map((o, index) => {
                      const optionStr: string = o as string;
                      return (
                        <li key={index} className={styles.questionBox}>
                          {optionStr}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
            {suggestBar}
            {hasLessonPresentationContents ? (
              <>
                {isAusPreProgramQuiz ? (
                  <>
                    <div className={styles.lessonButtonView}>
                      <div className={styles.subButtonsView}>
                        <div className={styles.subButton}>
                          <Button
                            size="small"
                            disabled={
                              !data2?.sllPreProgramQuiz ||
                              data2?.sllPreProgramQuiz.length === 0
                            }
                            label={`Preview ${grade?.label} Quiz`}
                            className={
                              !data2?.sllPreProgramQuiz ||
                              data2?.sllPreProgramQuiz.length === 0
                                ? styles.disableButton
                                : styles.previewButton
                            }
                            onClick={handleLessonPreview}
                            leftIcon={
                              loading2 ? (
                                <Spinner width={20} height={20} />
                              ) : null
                            }
                          />
                        </div>
                        <div className={styles.subButton}>
                          <Button
                            size="small"
                            disabled={
                              !data2?.sllPreProgramQuiz ||
                              data2?.sllPreProgramQuiz.length === 0
                            }
                            label={`Begin ${grade?.label} Quiz`}
                            className={
                              !data2?.sllPreProgramQuiz ||
                              data2?.sllPreProgramQuiz.length === 0
                                ? styles.disableButton
                                : styles.beginQuizButton
                            }
                            onClick={handleStartBegin}
                            leftIcon={
                              loading2 ? (
                                <Spinner width={20} height={20} />
                              ) : null
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div
                    className={styles.lessonButtonView}
                    style={{ position: isMobile ? "relative" : "sticky" }}
                  >
                    <div className={styles.subButtonsView}>
                      {!isMobile && !newLessonLayout && (
                        <div className={styles.subButton}>
                          <Button
                            size="small"
                            label={
                              isCaregiver ? "Preview Module" : "Preview Lesson"
                            }
                            className={styles.previewButton}
                            onClick={handleLessonPreview}
                          />
                        </div>
                      )}
                      <div className={styles.subButton}>
                        {100 > completedPercent && completedPercent > 0 ? (
                          <Dropdown
                            menu={{ items: items }}
                            trigger={["click"]}
                            placement="bottom"
                            onOpenChange={handleResumeDropdownVisible}
                            open={showResumeMenu}
                          >
                            <Button
                              size="small"
                              label={
                                isCaregiver ? "Begin Module" : "Begin Lesson"
                              }
                              className={styles.beginButton}
                            />
                          </Dropdown>
                        ) : (
                          <Button
                            size="small"
                            label={
                              isCaregiver ? "Begin Module" : "Begin Lesson"
                            }
                            className={styles.beginButton}
                            onClick={
                              isMobile ? handleLessonPreview : handleStartBegin
                            }
                          />
                        )}
                      </div>
                      {!(isCaregiver && isMobile) && (
                        <div>
                          <Tooltip
                            placement="topRight"
                            title={
                              <div className={styles.lessonTip}>
                                Share Lesson
                              </div>
                            }
                            arrowPointAtCenter
                            color="#FFFFFF"
                          >
                            <div
                              className={styles.shareButton}
                              onClick={handleOpenShareModal}
                            >
                              <ShareIcon />
                            </div>
                          </Tooltip>
                        </div>
                      )}
                      {!isCaregiver && (
                        <div>
                          <Menu items={burgeritems} mode="horizontal" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div
                className={styles.lessonButtonView}
                style={{ position: isMobile ? "relative" : "sticky" }}
              >
                <div className={styles.subButtonsView}>
                  {/*
                    A teacher can take the same lesson to more than one year group, and each
                    completion is what tells analytics which year it was taught to. Replacing this
                    button with Rate Lesson meant only the first year was ever recorded, so it
                    stays available and Rate joins it rather than taking its place.
                  */}
                  <div className={styles.subButton}>
                    <Button
                      disabled={loadingLessonComplete || isRefetching}
                      size="small"
                      label={"Mark as Complete"}
                      className={styles.previewButton}
                      onClick={handleCompleteLesson}
                      leftIcon={
                        loadingLessonComplete || isRefetching ? (
                          <Spinner width={20} height={20} />
                        ) : undefined
                      }
                    />
                  </div>
                  {completedPercent === 100 && (
                    <div className={styles.subButton}>
                      <div
                        className={styles.rateButton}
                        onClick={handleOpenFeedback}
                      >
                        <LordIcon
                          src={IconRateLesson}
                          isHover={true}
                          colors={{ primary: "#6BC690", secondary: "#6BC690" }}
                          size={24}
                          target=".LessonProgressModal_feedbackButton__KoIAL"
                        />
                        <span>Rate Lesson</span>
                      </div>
                    </div>
                  )}
                  {!isMobile && !newLessonLayout && (
                    <div className={styles.subButton}>
                      <Button
                        size="small"
                        disabled={lessonInfo?.materials?.length === 0}
                        label={isCaregiver ? "Begin Module" : "Begin Lesson"}
                        className={styles.beginButton}
                        onClick={handleStartBegin}
                      />
                    </div>
                  )}
                  {!(isCaregiver && isMobile) && (
                    <div>
                      <Tooltip
                        placement="topRight"
                        title={
                          <div className={styles.lessonTip}>Share Lesson</div>
                        }
                        arrowPointAtCenter
                        color="#FFFFFF"
                      >
                        <div
                          className={styles.shareButton}
                          onClick={handleOpenShareModal}
                        >
                          <ShareIcon />
                        </div>
                      </Tooltip>
                    </div>
                  )}
                  {!isCaregiver && (
                    <div>
                      <Menu items={burgeritems} mode="horizontal" />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Lesson Progress & Lesson Presentation Section */}
          {hasLessonPresentationContents && (
            <>
              {isAusPreProgramQuiz ? (
                <Dropdown
                  menu={{ items: gradeMenu, onClick: handleSelectGrade }}
                  trigger={["click"]}
                  placement="bottom"
                  onOpenChange={handleYearDropdownVisible}
                  open={showGradeMenu}
                >
                  <div className={styles.selectYearView}>
                    <div className={styles.gradeField}>
                      <div className={styles.gradeLabel}>{grade?.label}</div>
                      <div
                        className={[
                          styles.dropdownToggle,
                          showGradeMenu
                            ? styles.dropdownToggleClose
                            : styles.dropdownToggleOpen,
                        ].join(" ")}
                      >
                        <ChevronDown className={styles.icon} />
                      </div>
                    </div>
                  </div>
                </Dropdown>
              ) : (
                !isCaregiver &&
                lessonInfo?.status !== "" && (
                  <div className={styles.progressView}>
                    <div className={styles.progressHead}>
                      <h2>Lesson Progress</h2>
                    </div>
                    <div className={styles.progressBackBar}>
                      <div
                        className={styles.progressBar}
                        style={{
                          width: `${completedPercent}%`,
                          borderRadius: completedPercent >= 100 ? "3px" : "",
                        }}
                      ></div>
                      <div className={styles.iconView}>
                        {lessonSteps.map((item: any, index: number) => {
                          const Icon = getContentIcon(item.type);
                          return (
                            <div
                              key={item.id}
                              className={styles.lessonProgress}
                            >
                              {Icon && <Icon className={styles.moduleIcon} />}
                            </div>
                          );
                        })}
                      </div>
                      {completedPercent === 100 ? (
                        <Tooltip
                          placement="topRight"
                          title={
                            <div className={styles.lessonTip}>
                              Please click the button to share your thoughts on
                              the Lessons.
                            </div>
                          }
                          arrowPointAtCenter
                          overlayStyle={{ maxWidth: "529px" }}
                          color="#FFFFFF"
                        >
                          <div
                            className={styles.feedbackButton}
                            onClick={handleOpenFeedback}
                          >
                            <LordIcon
                              src={IconRateLesson}
                              isHover={true}
                              colors={{
                                primary: "#6BC690",
                                secondary: "#6BC690",
                              }}
                              size={24}
                              target=".LessonProgressModal_feedbackButton__KoIAL"
                            />
                            <span>Rate Lesson</span>
                          </div>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          placement="topRight"
                          title={
                            <div className={styles.lessonTip}>
                              Already Completed this Lesson? Click Here to Mark
                              it as Completed!
                            </div>
                          }
                          arrowPointAtCenter
                          overlayStyle={{ maxWidth: "529px" }}
                          color="#FFFFFF"
                        >
                          <div
                            className={styles.completedCheck}
                            onClick={handleCompleteLesson}
                          >
                            {loadingLessonComplete || isRefetching ? (
                              <LoadingOutlined
                                style={{ fontSize: 24, borderRadius: 15 }}
                                spin
                              />
                            ) : (
                              <NormalCheckIcon
                                className={styles.completeIcon}
                                width={32}
                                height={32}
                              />
                            )}
                          </div>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                )
              )}
            </>
          )}
          {lessonInfo?.materials && lessonInfo.materials.length > 0 && (
            <div id="lessonform" className={styles.lessonView}>
              <div className={styles.lessonSubView}>
                <div className={styles.lessonTitle}>
                  {isCaregiver ? "Downloadable Resources" : "Lesson Materials"}
                </div>
                <div className={styles.lessonListView}>
                  <LessonForm
                    lesson={lessonInfo}
                    handleDownloadMaterial={handleShowMaterial}
                  />
                </div>
                {showCheatSheet && <CheatSheetSection />}
                {lessonInfo?.showLessonMaterialsNotice &&
                  !isSurveyModuleOnly && (
                    <div className={styles.materialNote}>
                      <LordIcon
                        src={IconProjRequired}
                        isHover={true}
                        colors={{
                          primary: "#AF77B8",
                          secondary: "#AF77B8",
                        }}
                        stroke={5}
                        size={18}
                      />
                      <div className={styles.materialNoteText}>
                        This lesson <span>only</span> contains downloadable
                        materials.
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}
          <div className={styles.overview}>
            <div className={styles.lessonSubView}>
              {<div className={styles.lessonTitle}>Lesson Information</div>}
              <OverviewForm
                lesson={lessonInfo}
                skills={standaloneLessonsSelCompetencies}
                isAusPreProgramQuiz={isAusPreProgramQuiz}
              />
            </div>
          </div>
          {priorLessons.length > 0 && (
            <div className={styles.lessonView}>
              <div className={styles.lessonSubView}>
                <div className={styles.lessonTitle}>
                  Recommended Prior Learning
                </div>
                <div className={styles.contentView}>
                  <div className={styles.requirementContent}>
                    We recommend completing these lessons with students prior to
                    starting this Lesson.
                  </div>
                </div>
                <div className={styles.recommendLessonListView}>
                  {priorLessons.map((item, index) => {
                    return (
                      <Card
                        key={index}
                        lesson={item}
                        handleOpenLesson={openRecommendedLesson}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {nextLessons.length > 0 && (
            <div className={styles.lessonView}>
              <div className={styles.lessonSubView}>
                <div className={styles.lessonTitle}>
                  Recommended Next Lessons
                </div>
                <div className={styles.contentView}>
                  <div className={styles.requirementContent}>
                    These Lessons will build on the skills learned in this
                    lesson.
                  </div>
                </div>
                <div className={styles.recommendLessonListView}>
                  {nextLessons.map((item, index) => {
                    return (
                      <Card
                        key={index}
                        lesson={item}
                        handleOpenLesson={openRecommendedLesson}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <div className={styles.empty}>&nbsp;</div>
        </div>
      )}
      {showLessonView && content ? (
        <div className={`${styles.teacherFSView} ${styles.presentationOverlay}`}>
          <div className={styles.fsBackdrop} onClick={handleClose} />
          <div className={styles.lessonFSSubView}>
            <SelectedContent
              lesson={lessonInfo}
              content={content}
              goBack={handleCloseLesson}
              lastPageNum={lastPageNum}
              isParentApp={isParentApp}
            />
          </div>
        </div>
      ) : null}
      {showLessonPreView && content && lessonInfo ? (
        <div className={`${styles.teacherFSView} ${styles.presentationOverlay}`}>
          <div
            className={styles.fsBackdrop}
            onClick={handleCloseLessonPreview}
          />
          <div
            className={
              newLessonLayout
                ? styles.newLayoutFSSubView
                : styles.teacherFSSubView
            }
          >
            <LessonPreView
              lesson={lessonInfo}
              onClose={handleCloseLessonPreview}
              handleStartBegin={handleStartBegin}
              onExit={onClose}
              startLesson={handleStartLesson}
              handleResumeLesson={handleResumeLesson}
              isMaterials={isMaterials}
              isPlatformTower={isPlatformTower}
              isParentApp={isParentApp}
            />
          </div>
        </div>
      ) : null}
      {showQuizPreview && content && lessonInfo ? (
        <div className={`${styles.teacherFSView} ${styles.presentationOverlay}`}>
          <div
            className={styles.fsBackdrop}
            onClick={handleCloseLessonPreview}
          />
          <div className={styles.teacherFSSubView}>
            <QuizPreview
              lesson={lessonInfo}
              contents={data2?.sllPreProgramQuiz as SllModule[]}
              onClose={handleCloseLessonPreview}
              onExit={onClose}
            />
          </div>
        </div>
      ) : null}
      {resumeModal && (
        <div className={styles.resumeLessonView}>
          <div className={styles.fsBackdrop} onClick={handleCloseResumeModal} />
          <div className={styles.resumeLessonSubView}>
            <ResumeModal
              onClose={handleCloseResumeModal}
              onContinue={handleResumeLesson}
              onStart={handleStartBegin}
            />
          </div>
        </div>
      )}
      {
        <div
          className={styles.backTipView}
          style={{ display: backHover ? "flex" : "none" }}
        >
          <div className={styles.tipTextView}>
            <div className={styles.tipTitle}>{backTitle}</div>
          </div>
          <div className={styles.arrowDown}></div>
        </div>
      }
      {!(loading || loading1 || visitloading) &&
        showBackButton &&
        !isLessonOverlayActive && (
        <div className={styles.backButtonView}>
          <div
            className={styles.floatingBack}
            onClick={handleClose}
            onMouseOver={() => setBackHover(true)}
            onMouseLeave={() => setBackHover(false)}
          >
            <FSBackIcon width={36} height={36} />
          </div>
        </div>
      )}
      {!loading && courseMode === MyCourseEnum.Library && (
        <div className={styles.addView}>
          <div className={styles.courseListView}>
            <div className={styles.courseListTitle}>
              <p>
                Add to <br />
                Collection
              </p>
            </div>
            {coursesForSelectedLesson?.map((item, i) => {
              return (
                <Tooltip
                  placement="right"
                  title={
                    <div className={styles.lessonTip} key={i}>
                      {"View"} {`${item.name} Collection`}
                    </div>
                  }
                  arrowPointAtCenter
                  color="#FFFFFF"
                  key={i}
                >
                  <div
                    key={i}
                    className={styles.addItem}
                    style={{
                      background: `${item.darkColor}`,
                      cursor: "pointer",
                    }}
                    onClick={handleClickStudyPlan(item)}
                  >
                    <div className={styles.addTitle}>
                      {item.name.substring(0, 1).toLocaleUpperCase()}
                    </div>
                  </div>
                </Tooltip>
              );
            })}
            <Button
              label="+"
              color="white"
              size="small"
              className={styles.plusBtn}
              onClick={addLessonToCollection}
            />
          </div>
        </div>
      )}
      {showInstructionModal && (
        <RequiredLessonMaterials
          open={showInstructionModal}
          lesson={lessonInfo}
          isEducatorCaregiverLessonLayout={newLessonLayout}
          showLessonMaterialsNotice={
            (lessonInfo?.showLessonMaterialsNotice && !isSurveyModuleOnly) ??
            false
          }
          hasLessonPresentationContents={hasLessonPresentationContents}
          onClose={() => setShowInstructionModal(false)}
          onStartLesson={handleBeginLesson}
          onCompleteLesson={handleCompleteLesson}
          onUndoCompleteLesson={handleUndoCompleteLesson}
          completedPercent={completedPercent}
          openFeedback={handleOpenFeedback}
          handleDownloadMaterial={handleShowMaterial}
          bindCloseHandler={bindMaterialsCloseHandler}
        />
      )}
      {showLessonFeedback ? (
        <div className={styles.teacherFSView}>
          <div
            className={styles.fsBackdrop}
            onClick={handleCloseLessonFeedback}
          />
          <div className={styles.teacherFSSubViewTypeForm}>
            <Widget
              id={"Yg59zfcB"}
              hidden={{
                account: user?.customer?.name ?? "",
                school: user?.schools?.[0]?.name ?? "",
                course: "standalone",
                unit: "",
                country: lessonInfo?.country ?? "",
                lesson: lessonInfo?.name ?? "",
                email: user?.email ?? "",
              }}
              onSubmit={handleCloseLessonFeedback}
            />
          </div>
        </div>
      ) : null}
      {openShareModal && (
        <ShareModal
          open={openShareModal}
          title={`Share Lesson with`}
          shareId={lessonId}
          isSll={true}
          onClose={() => setOpenShareModal(false)}
          continueWithTeacher={handleShareTeacher}
          continueWithStudent={handleShareStudent}
        />
      )}
      {shareLesson && (
        <NormalModal
          logoIcon={<GreenShareIcon />}
          onClose={() => {
            setShareLesson(false);
          }}
          title={<div className={styles.modalTitle}>Share This Lesson</div>}
          open={shareLesson}
          description={
            <div className={styles.modalDetail}>
              {isCaregiver || isAusPreProgramQuiz
                ? "Use this link to share this lesson with a parent or caregiver. They do not require an Open Parachute account to access this lesson."
                : "Use this link to share this Lesson with a colleague. They will need an Open Parachute account to access the Lesson."}
            </div>
          }
          leftButton={
            <Button
              isLoading={generating}
              className={styles.cancelButton}
              onClick={() => {
                setShareLesson(false);
              }}
              label="Cancel"
            />
          }
          rightButton={
            <CopyToClipboard
              text={
                // Carry the grade of the card this was opened from, so a shared link records
                // the year taught exactly as clicking the card would have.
                createPrettyURL(`standalone/${lessonInfo?.uniqueName}`) +
                (gradeName ? `?grade=${encodeURIComponent(gradeName)}` : "")
              }
            >
              <Button
                isLoading={generating}
                className={styles.copyButton}
                onClick={handleCopyToClipboard}
                label="Copy Link"
              />
            </CopyToClipboard>
          }
        />
      )}
      {showExitModal && exitTicketActive && (
        <GenerateExitTicketLinkModal
          open={showExitModal}
          onClose={() => setShowExitModal(false)}
          onActiveExitTicket={handleActiveFormPreview}
          formData={exitTicketActive.formData}
          isSll
          sllLesson={lessonInfo}
          sllCourse={course}
          ausPreProgram={isAusPreProgramQuiz}
          gradeName={grade?.label}
        />
      )}
      {showStudentLinkModal && (
        <GenerateStudentLinkModal
          open={showStudentLinkModal}
          lessonId={lessonId}
          isSll={true}
          onClose={() => setShowStudentLinkModal(false)}
          onActiveStudentLink={handleActiveStudentLink}
          formData={exitTicketActive?.formData}
          gradeName={grade?.label}
        />
      )}
      {showMaterail && content ? (
        <div className={styles.teacherFSView}>
          <div
            className={styles.fsBackdrop}
            onClick={handleCloseMaterialView}
          />
          <div className={styles.teacherFSSubView}>
            <SllMaterialPreview
              needPrint={needPrint}
              onClose={handleCloseMaterialView}
              onShare={
                lessonInfo?.lessonType === "5"
                  ? handleOpenShareModal
                  : undefined
              }
            />
          </div>
        </div>
      ) : null}
    </LessonModal>
  );
}

const LessonProgressModal = (props: Props) => {
  const {
    open,
    onClose,
    lessonId,
    lesson,
    lessonlist,
    courseMode,
    course,
    isParentApp,
    addLessonToCollection,
    coursesForSelectedLesson,
    gotoCollection,
    isPlatformTower,
    gradeName,
    isAusPreProgramQuiz,
    showBackButton,
    programName,
    fireEvent,
    programId,
    mcId,
    mcName,
    launchedGrade,
    collectionId,
    collectionName,
  } = props;

  return (
    <CourseContentProvider>
      <LessonProgressComponent
        open={open}
        onClose={onClose}
        lessonId={lessonId}
        lesson={lesson}
        lessonlist={lessonlist}
        courseMode={courseMode}
        course={course}
        isParentApp={isParentApp}
        addLessonToCollection={addLessonToCollection}
        coursesForSelectedLesson={coursesForSelectedLesson}
        gotoCollection={gotoCollection}
        isPlatformTower={isPlatformTower}
        isAusPreProgramQuiz={isAusPreProgramQuiz}
        programName={programName}
        showBackButton={showBackButton}
        fireEvent={fireEvent}
        gradeName={gradeName}
        programId={programId}
        mcId={mcId}
        mcName={mcName}
        launchedGrade={launchedGrade}
        collectionId={collectionId}
        collectionName={collectionName}
      />
    </CourseContentProvider>
  );
};

export default LessonProgressModal;
